import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { PexIcon } from './icons/PexIcon'
import { fetchSellerStores, type SellerStoreRow } from '../api/seller-listings'
import { USE_REAL_API } from '../lib/api'
import { useT } from '../i18n'
import { shellHeaderI18n } from '../i18n/shared/shell-header'
import { getStoreProvider } from '../features/seller/stores/store-providers'
import { ProviderMark } from '../features/seller/stores/ProviderMark'
import { cn } from '../lib/utils'

const STORE_NAME_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

const PROVIDER_DOT_CLASS: Record<string, string> = {
  amazon: 'bg-[#ff9900]',
  ebay: 'bg-[#4777c7]',
  etsy: 'bg-[#f1641e]',
  shopify: 'bg-[#95bf47]',
}

function getProviderPriority(provider: string) {
  switch (provider.toLowerCase()) {
    case 'etsy':
      return 0
    case 'shopify':
      return 1
    case 'amazon':
      return 2
    case 'ebay':
      return 4
    default:
      return 3
  }
}

function groupStoresByProvider<
  T extends { provider: string; shop_name: string },
>(stores: T[]) {
  const groups = new Map<string, T[]>()

  for (const store of stores) {
    const providerKey = store.provider.toLowerCase()
    const providerStores = groups.get(providerKey)
    if (providerStores) {
      providerStores.push(store)
    } else {
      groups.set(providerKey, [store])
    }
  }

  return Array.from(groups, ([provider, providerStores]) => ({
    provider,
    label: getStoreProvider(provider)?.name ?? provider,
    stores: providerStores.sort((a, b) =>
      STORE_NAME_COLLATOR.compare(a.shop_name, b.shop_name),
    ),
  })).sort((a, b) => {
    const priorityDifference =
      getProviderPriority(a.provider) - getProviderPriority(b.provider)
    return priorityDifference || STORE_NAME_COLLATOR.compare(a.label, b.label)
  })
}

type PopupPosition = {
  left: number
  maxHeight: number
  top: number
  width: number
}

// Seller shell store selector. It reads the same real /store-integrations cache
// as Settings and My Listings, and lists every active integration regardless of
// token state. The popup is portalled so the scrolling sidebar cannot clip it.
export function StoreSelector() {
  const t = useT(shellHeaderI18n).storeSelector
  const listboxId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null)

  const stores = useQuery({
    queryKey: ['seller', 'stores'],
    queryFn: fetchSellerStores,
    enabled: USE_REAL_API,
  })

  const rows = (stores.data?.items ?? []).filter(
    (store) => store.active !== false,
  )
  const selected = rows.find((store) => store.id === selectedId) ?? rows[0]
  const selectedProvider = selected
    ? getStoreProvider(selected.provider)
    : undefined
  const showSearch = rows.length > 8
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const providerGroups = useMemo(() => {
    const visibleRows = normalizedQuery
      ? rows.filter((store) => {
          const providerName =
            getStoreProvider(store.provider)?.name ?? store.provider
          return `${store.shop_name} ${providerName}`
            .toLocaleLowerCase()
            .includes(normalizedQuery)
        })
      : rows
    return groupStoresByProvider(visibleRows)
  }, [normalizedQuery, rows])
  const visibleStores = providerGroups.flatMap((group) => group.stores)
  const effectiveActiveId = visibleStores.some((store) => store.id === activeId)
    ? activeId
    : (visibleStores[0]?.id ?? null)

  function updatePopupPosition() {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const viewportPadding = 12
    const top = Math.round(rect.bottom + 6)
    const preferredWidth = Math.max(rect.width, 240)
    const width = Math.min(
      preferredWidth,
      window.innerWidth - viewportPadding * 2,
    )
    const left = Math.min(
      Math.max(viewportPadding, Math.round(rect.left)),
      window.innerWidth - width - viewportPadding,
    )

    setPopupPosition({
      left,
      maxHeight: Math.max(
        80,
        Math.floor(window.innerHeight - top - viewportPadding),
      ),
      top,
      width,
    })
  }

  function closePopup({ restoreFocus = true } = {}) {
    setOpen(false)
    setQuery('')
    setActiveId(null)
    if (restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }

  function openPopup(preferredActiveId = selected?.id ?? rows[0]?.id ?? null) {
    setActiveId(preferredActiveId)
    setOpen(true)
  }

  function selectStore(store: SellerStoreRow) {
    setSelectedId(store.id)
    closePopup()
  }

  function moveActive(delta: number) {
    if (visibleStores.length === 0) return
    setActiveId((currentId) => {
      const currentIndex = visibleStores.findIndex(
        (store) => store.id === currentId,
      )
      if (currentIndex < 0)
        return delta > 0 ? visibleStores[0].id : visibleStores.at(-1)!.id
      return visibleStores[
        (currentIndex + delta + visibleStores.length) % visibleStores.length
      ].id
    })
  }

  function handlePopupKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveActive(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveActive(-1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveId(visibleStores[0]?.id ?? null)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveId(visibleStores.at(-1)?.id ?? null)
    } else if (event.key === 'Enter') {
      const activeStore = visibleStores.find(
        (store) => store.id === effectiveActiveId,
      )
      if (activeStore) {
        event.preventDefault()
        selectStore(activeStore)
      }
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closePopup()
    } else if (event.key === 'Tab') {
      closePopup({ restoreFocus: false })
    }
  }

  useLayoutEffect(() => {
    if (!open) return
    updatePopupPosition()
    const update = () => updatePopupPosition()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  useEffect(() => {
    if (!open || !popupPosition) return
    const focusTarget = showSearch ? searchRef.current : listboxRef.current
    focusTarget?.focus()
  }, [open, popupPosition, showSearch])

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !popupRef.current?.contains(target)
      ) {
        closePopup({ restoreFocus: false })
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (!open || !effectiveActiveId) return
    document
      .getElementById(`${listboxId}-option-${effectiveActiveId}`)
      ?.scrollIntoView?.({
        block: 'nearest',
      })
  }, [effectiveActiveId, listboxId, open])

  if (stores.isLoading && USE_REAL_API) {
    return (
      <div
        aria-hidden="true"
        className="h-9 animate-pulse rounded-md border border-[var(--sidebar-divider)] bg-[var(--sidebar-control-bg)]"
      />
    )
  }

  if (rows.length === 0) {
    return (
      <Link
        to="/seller/settings"
        search={{ section: 'store-integrations' }}
        className="flex items-center gap-2 rounded-md border border-dashed border-[var(--sidebar-divider)] px-2.5 py-1.5 text-sm text-[var(--sidebar-child-text)] hover:bg-[var(--sidebar-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]"
      >
        <PexIcon name="add" className="text-[var(--sidebar-icon)]" size="xs" tone="inherit" />
        <span className="flex-1 truncate">{t.noStores}</span>
        <span className="text-xs font-medium text-[var(--sidebar-parent-text)]">
          {t.addStore}
        </span>
      </Link>
    )
  }

  const popup =
    open && popupPosition
      ? createPortal(
          <div
            ref={popupRef}
            style={{
              left: popupPosition.left,
              maxHeight: popupPosition.maxHeight,
              top: popupPosition.top,
              width: popupPosition.width,
              // The mobile navigation drawer owns z-50. This portalled popup
              // must sit above it as well as above ordinary page dropdowns.
              zIndex: 'var(--z-modal)',
            }}
            className="fixed flex overflow-hidden rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-float)]"
            onKeyDown={handlePopupKeyDown}
          >
            <div className="flex min-h-0 w-full flex-col">
              {showSearch ? (
                <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] p-2">
                  <label className="sr-only" htmlFor={`${listboxId}-search`}>
                    {t.searchStores}
                  </label>
                  <div className="flex min-h-10 items-center gap-2 rounded-[var(--radius-control)] border border-[var(--input-border)] bg-[var(--input-bg)] px-3 transition focus-within:border-[var(--color-focus)] focus-within:shadow-[var(--shadow-focus)]">
                    <PexIcon name="search" size="xs" tone="muted" />
                    <input
                      ref={searchRef}
                      id={`${listboxId}-search`}
                      type="search"
                      role="searchbox"
                      autoComplete="off"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder={t.searchStores}
                      aria-controls={listboxId}
                      aria-activedescendant={
                        effectiveActiveId
                          ? `${listboxId}-option-${effectiveActiveId}`
                          : undefined
                      }
                      className="min-w-0 flex-1 bg-transparent text-sm text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-muted)]"
                    />
                  </div>
                </div>
              ) : null}

              {visibleStores.length === 0 ? (
                <div
                  id={listboxId}
                  role="listbox"
                  aria-label={t.label}
                  className="min-h-0 flex-1 overflow-y-auto"
                >
                  <p className="px-3 py-6 text-center text-sm text-[var(--color-text-muted)]">
                    {t.noSearchResults}
                  </p>
                </div>
              ) : (
                <div
                  ref={listboxRef}
                  id={listboxId}
                  role="listbox"
                  aria-label={t.label}
                  aria-activedescendant={
                    !showSearch && effectiveActiveId
                      ? `${listboxId}-option-${effectiveActiveId}`
                      : undefined
                  }
                  tabIndex={showSearch ? undefined : -1}
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5 outline-none"
                >
                  {providerGroups.map((group, groupIndex) => (
                    <div
                      key={group.provider}
                      role="group"
                      aria-label={group.label}
                      className={cn(
                        'py-1',
                        groupIndex > 0 &&
                          'mt-1 border-t border-[var(--color-border)] pt-2',
                      )}
                    >
                      <div className="flex min-h-7 items-center gap-2 px-2 text-[10px] font-bold uppercase tracking-[0.11em] text-[var(--color-text-muted)]">
                        <span
                          aria-hidden="true"
                          className={cn(
                            'h-1.5 w-1.5 rounded-full bg-slate-400',
                            PROVIDER_DOT_CLASS[group.provider],
                          )}
                        />
                        <span>{group.label}</span>
                        <span className="ml-auto font-medium tracking-normal">
                          {group.stores.length}
                        </span>
                      </div>
                      {group.stores.map((store) => {
                        const isSelected = store.id === selected?.id
                        const isActive = store.id === effectiveActiveId
                        return (
                          <button
                            key={store.id}
                            id={`${listboxId}-option-${store.id}`}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            tabIndex={-1}
                            onPointerMove={() => setActiveId(store.id)}
                            onClick={() => selectStore(store)}
                            className={cn(
                              'relative flex min-h-9 w-full items-center rounded-[var(--radius-control)] px-2.5 text-left text-sm text-[var(--color-text)] outline-none transition',
                              isSelected &&
                                'bg-[var(--color-warm-selected)] font-semibold',
                              isActive &&
                                !isSelected &&
                                'bg-[var(--color-surface-muted)]',
                              'focus-visible:shadow-[var(--shadow-focus)]',
                            )}
                          >
                            {isSelected ? (
                              <span
                                aria-hidden="true"
                                className="absolute left-0 h-4 w-[3px] rounded-full bg-[var(--color-signal)]"
                              />
                            ) : null}
                            <span className="min-w-0 flex-1 truncate">
                              {store.shop_name}
                            </span>
                            {isSelected ? (
                              <PexIcon name="check" className="ml-2 text-[var(--color-signal)]" size="xs" tone="inherit" />
                            ) : null}
                          </button>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <div className="flex items-center gap-2">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={t.label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => (open ? closePopup() : openPopup())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            const preferredId =
              event.key === 'ArrowUp' ? visibleStores.at(-1)?.id : selected?.id
            openPopup(preferredId)
          }
        }}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-control)] border border-[var(--sidebar-divider)] bg-[var(--sidebar-control-bg)] px-2.5 text-left outline-none transition hover:bg-[var(--sidebar-control-hover-bg)] focus-visible:shadow-[var(--sidebar-focus-shadow)]"
      >
        {selectedProvider ? (
          <ProviderMark provider={selectedProvider} size="sm" />
        ) : (
          <PexIcon name="store" className="text-[var(--sidebar-icon)]" size="xs" tone="inherit" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--sidebar-child-text)]">
            {t.activeStore}
          </span>
          <span className="block truncate text-sm font-semibold text-[var(--sidebar-parent-text)]">
            {selected?.shop_name}
          </span>
        </span>
        <PexIcon name="caretDown" className={cn(
 'h-4 w-4 shrink-0 text-[var(--sidebar-icon)] transition-transform',
 open && 'rotate-180',
 )} size="xs" tone="inherit" />
      </button>
      <Link
        to="/seller/settings"
        search={{ section: 'store-integrations' }}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-[var(--sidebar-icon)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-parent-text)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]"
        aria-label={t.addStore}
        title={t.addStore}
      >
        <PexIcon name="add" size="xs" tone="inherit" />
      </Link>
      {popup}
    </div>
  )
}
